/**
 * Comparing one template name with another, the two ways the product does it.
 *
 * Both are the same question — "are these the same name to a person?" — asked
 * once of a search box and once of a duplicate warning, so they share one
 * normalisation rather than two that drift. A coach who typed "Sección" once and
 * "seccion" the next time meant the same thing both times, and the product has
 * to agree with her in both places.
 *
 * Pure, and here rather than in the screen, so the awkward cases — accents,
 * capitals, a stray double space — are asserted against the functions instead of
 * against a rendered list.
 */

/**
 * Lower-case, unaccented, trimmed, with runs of whitespace collapsed.
 *
 * The decomposition-then-strip is the same one `exercisePicker` uses: NFD splits
 * "ó" into "o" plus a combining acute, and the acute is then removed. It is
 * deliberately naive about scripts the product does not serve — the coach and
 * her clients write Castilian and Catalan — and naive is right here, because the
 * alternative is a collation dependency for a search box.
 */
export function normaliseName(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Does this template name match what the coach typed?
 *
 * A substring rather than a word prefix, and that is the difference from the
 * exercise picker on purpose: she named these herself and is recognising one she
 * already knows, not discovering one of 165 rows she has never seen. "lower"
 * finds "Lower body A"; an empty query matches everything.
 */
export function matchesName(name: string, query: string): boolean {
  const needle = normaliseName(query);
  if (needle === '') return true;
  return normaliseName(name).includes(needle);
}

/** Whether the coach already has a template a person would read as this name. */
export function isDuplicateName(existing: readonly string[], candidate: string): boolean {
  const wanted = normaliseName(candidate);
  if (wanted === '') return false;
  return existing.some((name) => normaliseName(name) === wanted);
}

/**
 * How many templates it takes before the search field appears.
 *
 * The spec asks for it "once the list is long enough that it does not fit on one
 * screen", which no component can actually measure without measuring — and a
 * field that appears and disappears as the window is resized is worse than one
 * that appears at a number. Ten rows is about a laptop screen of this list, and
 * the coach is expected to have a dozen or so templates in total, so the field
 * arrives roughly when she starts having to scroll for one.
 */
export const SEARCH_APPEARS_AT = 10;
